import React, { useState, useEffect } from "react";
import AdminLayout from "./AdminLayout";
import "./App.css";
import { API_BASE_URL, authHeaders } from "./apiConfig";

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [screens, setScreens] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [newRoleName, setNewRoleName] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  // Load roles + screen list once
  useEffect(() => {
    fetch(`${API_BASE_URL}/permission/roles`)
      .then((r) => r.json())
      .then((data) => {
        setRoles(data.roles || []);
        setScreens(data.screens || []);
        if (data.roles && data.roles.length > 0) setSelectedRole(data.roles[0]);
      });
  }, []);

  // Load the permission matrix whenever the selected role changes
  useEffect(() => {
    if (!selectedRole) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/permission?role=${encodeURIComponent(selectedRole)}`)
      .then((r) => r.json())
      .then((data) => setPermissions(data))
      .finally(() => setLoading(false));
  }, [selectedRole]);

  const toggleCell = (screenKey, field) => {
    setPermissions((prev) =>
      prev.map((row) =>
        row.screenKey === screenKey ? { ...row, [field]: !row[field] } : row
      )
    );
  };

  const toggleAllForScreen = (screenKey, value) => {
    setPermissions((prev) =>
      prev.map((row) =>
        row.screenKey === screenKey
          ? { ...row, canView: value, canAdd: value, canEdit: value, canDelete: value }
          : row
      )
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    try {
      const res = await fetch(`${API_BASE_URL}/permission`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ role: selectedRole, permissions }),
      });
      if (res.ok) {
        setMessage("Permissions saved successfully.");
      } else {
        setMessage("Could not save permissions.");
      }
    } catch {
      setMessage("Unable to reach server.");
    } finally {
      setSaving(false);
    }
  };

  const handleUseNewRole = () => {
    const trimmed = newRoleName.trim();
    if (!trimmed) return;
    if (!roles.includes(trimmed)) setRoles([...roles, trimmed]);
    setSelectedRole(trimmed);
    setNewRoleName("");
  };

  return (
    <AdminLayout>
      <style>{`
        .role-toolbar { display: flex; gap: 1rem; align-items: flex-end; flex-wrap: wrap; margin-bottom: 1.5rem; }
        .role-toolbar .form-group { margin: 0; }
        .perm-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
        .perm-table th, .perm-table td { border: 1px solid #e2e8f0; padding: 8px 10px; text-align: center; }
        .perm-table th { background: #f8fafc; color: #334155; font-size: 0.8rem; text-transform: uppercase; }
        .perm-table td.screen-name { text-align: left; font-weight: 500; color: #0f172a; }
        .perm-table input[type="checkbox"] { width: 16px; height: 16px; cursor: pointer; }
        .perm-quick-toggle { font-size: 0.75rem; color: #2563eb; cursor: pointer; text-decoration: underline; background: none; border: none; padding: 0; margin-left: 6px; }
      `}</style>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Role &amp; Permission Management</h2>
        <p style={{ color: "#64748b", fontSize: "0.9rem" }}>
          Choose a role, tick the rights it should have on each screen, then save.
        </p>

        <div className="role-toolbar">
          <div className="form-group">
            <label>Select Role</label>
            <select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)}>
              {roles.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Or create new role</label>
            <div style={{ display: "flex", gap: "6px" }}>
              <input
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="e.g. Accountant"
              />
              <button type="button" className="btn btn-secondary" onClick={handleUseNewRole}>
                Add
              </button>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || !selectedRole}
          >
            {saving ? "Saving..." : "Save Permissions"}
          </button>
        </div>

        {message && <p>{message}</p>}

        {loading ? (
          <p>Loading permissions...</p>
        ) : (
          <table className="perm-table">
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Screen</th>
                <th>View</th>
                <th>Add</th>
                <th>Edit</th>
                <th>Delete</th>
                <th>Quick</th>
              </tr>
            </thead>
            <tbody>
              {permissions.map((row) => (
                <tr key={row.screenKey}>
                  <td className="screen-name">{row.screenLabel}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={row.canView}
                      onChange={() => toggleCell(row.screenKey, "canView")}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={row.canAdd}
                      onChange={() => toggleCell(row.screenKey, "canAdd")}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={row.canEdit}
                      onChange={() => toggleCell(row.screenKey, "canEdit")}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={row.canDelete}
                      onChange={() => toggleCell(row.screenKey, "canDelete")}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="perm-quick-toggle"
                      onClick={() => toggleAllForScreen(row.screenKey, true)}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      className="perm-quick-toggle"
                      onClick={() => toggleAllForScreen(row.screenKey, false)}
                    >
                      None
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
};

export default RoleManagement;